import { ChangeEvent, useState } from "react";

import { Link } from "react-router-dom";
import { toast } from "react-toastify";

import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Button } from "primereact/button";
import { Column } from "primereact/column";
import {
  DataTable,
  DataTableFilterEvent,
  DataTablePageEvent,
  DataTableSortEvent,
} from "primereact/datatable";
import { InputText } from "primereact/inputtext";
import { Message } from "primereact/message";

import GridActionButtons from "../../../common/components/GridActionButtons";
import Loader from "../../../common/components/Loader.tsx";
import { GridType, Permissions } from "../../../common/enums/sharedEnums.ts";
import { LazyTableState } from "../../../common/interfaces.ts";
import { IBuilding, IResponsePaginateServices } from "../reducers";
import {
  BUILDING_DETAILS,
  NEW_BUILDING,
  UPDATE_BUILDING,
} from "../routes/index.ts";
import { deleteBuilding, getBuildings } from "../services";


const defaultLazyData: LazyTableState = {
  first: 0,
  rows: 10,
  page: 0,
  filter: "",
  filters: {
    datedReference: { value: null, matchMode: "startsWith" },
    agency: { value: null, matchMode: "startsWith" },
    lessor: { value: null, matchMode: "startsWith" },
    building: { value: null, matchMode: "startsWith" },
    address: { value: null, matchMode: "startsWith" },
    managementLotNumber: { value: null, matchMode: "startsWith" },
  },
};

const BuildingsComponent = () => {
  const queryClient = useQueryClient();
  const [lazyState, setLazyState] = useState<LazyTableState>(defaultLazyData);
  const { data, isLoading, error, isFetching } = useQuery<
    IResponsePaginateServices,
    Error
  >(["orders", lazyState], () => getBuildings(lazyState), {
    keepPreviousData: true,
  });

  if (error) {
    console.log("Error :", error);
    return (
      <Message
        severity="error"
        text={
          "Erreur: Impossible de récupérer la liste de mes commandes.\nVeuillez contacter votre administrateur système."
        }
      />
    );
  }
  const onPageHandler = (event: DataTablePageEvent) =>
    setLazyState((prev) => {
      return { ...prev, ...event };
    });

  const onSortHandler = (event: DataTableSortEvent) =>
    setLazyState((prev) => {
      return { ...prev, multiSortMeta: event?.multiSortMeta || [] };
    });

  const onFilterHandler = (event: DataTableFilterEvent) =>
    setLazyState((prev) => {
      return { ...prev, filters: event.filters };
    });

  const clearFilterHandler = () => setLazyState(defaultLazyData);

  const removeBuilding = useMutation({
    mutationFn: (buildingId: string) => deleteBuilding(buildingId),
    onError: (error) => {
      console.error(error);
      toast.error("Erreur lors de la suppression du bien");
    },
    onSuccess: async () => {
      toast.success("L'immeuble a été supprimé avec succès");
      queryClient.invalidateQueries({ queryKey: ["orders", lazyState] });
    },
  });

  return (
    <div className="d-flex flex-column gap-3">
      <h1>Mes immeubles</h1>
      <div className="my-3">
        <Link to={NEW_BUILDING}>
          <Button
            type="button"
            severity="success"
            className="btn-add-new btn-success"
          >
            <i className="bi bi-plus" />
            Ajouter un immeuble
          </Button>
        </Link>
      </div>
      <div className="d-flex justify-content-between gap-3">
        <div className="p-input-icon-left w-100">
          <i className="pi pi-search" />
          <InputText
            className="w-100"
            value={lazyState.filter}
            onChange={(e: ChangeEvent<HTMLInputElement>) =>
              setLazyState((prev) => {
                return { ...prev, filter: e.target.value };
              })
            }
            placeholder="Rechercher"
          />
        </div>
        <Button
          type="button"
          icon="pi pi-filter-slash"
          label="Effacer"
          onClick={clearFilterHandler}
        />
      </div>
      <DataTable
        value={data?.buildings || []}
        loading={isLoading || isFetching}
        loadingIcon={<Loader />}
        lazy
        size={"small"}
        removableSort
        sortMode="multiple"
        multiSortMeta={lazyState.multiSortMeta}
        filterDisplay="row"
        filters={lazyState.filters}
        onPage={onPageHandler}
        onSort={onSortHandler}
        onFilter={onFilterHandler}
        paginator
        first={lazyState.first}
        rows={lazyState.rows}
        totalRecords={data?.count}
        rowsPerPageOptions={[10, 20, 30]}
        emptyMessage={"Aucun résultat trouvé :/"}
        scrollable
      >
        <Column
          header="Réf"
          field="datedReference"
          body={(data: IBuilding) =>
            data?.datedReference ? (
              <Link
                to={BUILDING_DETAILS.replace(
                  ":id",
                  data.datedReference || data.id || "",
                )}
                className="propertyAdressLink  link-td text-center d-flex flex-row justify-content-center gap-2"
              >
                {data.datedReference}
              </Link>
            ) : (
              "-"
            )
          }
          sortable
          filter
          showFilterMenu={false}
        ></Column>
        <Column
          header="Agence"
          field="agency"
          body={(data: IBuilding) => (data?.agency ? data.agency.label : "-")}
          sortable
          filter
          showFilterMenu={false}
        ></Column>
        <Column
          header="Bailleur"
          field="lessor"
          body={(data: IBuilding) => (data?.lessorName ? data.lessorName : "-")}
          sortable
          filter
          showFilterMenu={false}
        ></Column>
        <Column
          header="Nom Résidence/Programme"
          field="building"
          body={(data: IBuilding) =>
            data?.address?.residenceName ? (
              data.permissions.includes(Permissions.BUILDING_DWELLING_GET) ? (
                <Link
                  to={BUILDING_DETAILS.replace(
                    ":id",
                    data.datedReference || data.id || "",
                  )}
                  className="propertyAdressLink  link-td text-center d-flex flex-row justify-content-center gap-2"
                >
                  {data.address.residenceName}
                </Link>
              ) : (
                <span className="propertyAdressLink  link-td text-center d-flex flex-row justify-content-center gap-2">
                  {data.address.residenceName}
                </span>
              )
            ) : (
              "-"
            )
          }
          sortable
          filter
          showFilterMenu={false}
        ></Column>
        <Column
          style={{ minWidth: "150px" }}
          header="Adresse"
          field="address"
          body={(data: IBuilding) =>
            data?.address ? (
              <Link
                to={BUILDING_DETAILS.replace(
                  ":id",
                  data.datedReference || data.id || "",
                )}
                className="propertyAdressLink  link-td text-center d-flex flex-row justify-content-center gap-2"
              >
                {data.address.address} {data.address.zip} {data.address.city}
              </Link>
            ) : (
              "-"
            )
          }
          sortable
          filter
          showFilterMenu={false}
        ></Column>
        <Column
          header="N° de lots"
          field="managementLotNumber"
          body={(data: IBuilding) =>
            data?.managementLotNumber ? data.managementLotNumber : "-"
          }
          sortable
          filter
          showFilterMenu={false}
        ></Column>
        <Column
          header="Actions"
          frozen
          alignFrozen="right"
          body={(data: IBuilding) => (
            <div>
              <GridActionButtons
                {...(data.permissions.includes(
                  Permissions.BUILDING_DWELLING_GET,
                ) && {
                  viewUrl: BUILDING_DETAILS.replace(
                    ":id",
                    data.datedReference || data.id || "",
                  ),
                  downloadablePath: data?.files?.[0]?.path,
                })}
                {...(data.permissions.includes(
                  Permissions.BUILDING_DWELLING_UPDATE,
                ) && {
                  editUrl: UPDATE_BUILDING.replace(
                    ":id",
                    data.datedReference || data.id || "",
                  ).replace(":isAssociatingBuilding", "false"),
                  ...(data.id && {
                    remove: () => removeBuilding.mutate(data.id as string),
                  }),
                })}
                type={GridType.building}
              />
            </div>
          )}
        ></Column>
      </DataTable>
    </div>
  );
};

export default BuildingsComponent;
